import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { fetchMyPlots } from "../api/api";
import { useAuth } from "./AuthContext";

// Holds the logged-in customer's full plot list plus which one is
// "currently selected" — a customer can own more than one plot (e.g.
// 1A/1B/1C split from one Zoho row), and every plot-scoped screen (My
// Farm, Visit booking, NOC, Maintenance) should act on whichever one the
// customer picked here instead of each independently defaulting to the
// first plot returned by the API.
const PlotContext = createContext({});

export function PlotProvider({ children }) {
  const { user } = useAuth();
  const [plots, setPlots] = useState([]);
  const [selectedPlotId, setSelectedPlotId] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user) {
      setPlots([]);
      setSelectedPlotId(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchMyPlots();
      setPlots(data);
      setSelectedPlotId((prev) => (prev && data.some((p) => p.id === prev) ? prev : data[0]?.id ?? null));
    } catch {
      setPlots([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    reload();
  }, [reload]);

  function selectPlot(id) {
    setSelectedPlotId(id);
  }

  const selectedPlot = plots.find((p) => p.id === selectedPlotId) || null;

  return (
    <PlotContext.Provider value={{ plots, selectedPlotId, selectedPlot, selectPlot, loading, reload }}>
      {children}
    </PlotContext.Provider>
  );
}

export const usePlots = () => useContext(PlotContext);
